import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Loader2, TriangleAlert, Users } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { ActionButton, EmptyState, Field, FieldError, SecondaryAction } from "../kit";
import LogMatchGroundStep from "../LogMatchGroundStep";
import LogMatchAddGroundPanel from "../LogMatchAddGroundPanel";
import LoadFailedCard from "./LoadFailedCard";
import api from "../../lib/api";
import { getGame, scheduleGame, updateGame, gameDateLabel } from "../../lib/coachGames";
import { squadFiguresLine } from "../../lib/pickXiFigures";
import { fetchPlayerSummaries } from "../../lib/squadAverage";
import { planDateMax, planDateLabel } from "../../lib/comingUp";
import { todayIso } from "../../lib/trainingStatus";
import { useFocusFirstInvalid } from "../../lib/focusFirstInvalid";

const NO_PRESET = { groundId: null, name: null, latitude: null, longitude: null };
const GAMES_PATH = "/coach-portal/games";

/**
 * Issue #1316 (Pick XI coach 1 of 3; D297, D302) — Schedule a game, drawn in
 * `CoachScheduleGame.dc.html` (round 13). The fields are the player's Plan a
 * match (`PlanMatchScreen`) plus Competition (D255), then the squad: every
 * player linked to this coach, ticked or not, each with their figures.
 *
 * Saving is `POST /coach-games` with the ticked ids as `squad`. With `gameId`
 * (`?game=` on the route; Q4 recommendation, S1) the form opens prefilled
 * from `GET /coach-games/:id` and saves with `PUT`, the button keeping the
 * board's words. The squad can see the game in Coming up from the moment it
 * saves; the team only when it is published (#1318).
 *
 * States, each its own (D53): the squad is `loading`, `failed` (Try again),
 * `no-players`, or ready; a save is `saving`, `save-failed` (in place, what
 * was typed stays, D57) or `saved`, which replaces the form with the sentence
 * and the one gold action, Pick the XI. A game needs a squad, so Schedule
 * game waits until at least one player is ticked (F6, orchestrator call).
 *
 * The ground opens the log flow's ground step in place, as on Plan a match.
 */
export default function ScheduleGameForm({ gameId = null }) {
  const navigate = useNavigate();

  const [view, setView] = useState("form"); // "form" | "ground" | "add-ground"
  const [addSeed, setAddSeed] = useState("");
  const [form, setForm] = useState({ date: "", time: "", opposition: "", team: "", competition: "" });
  const [ground, setGround] = useState(null); // { name, id }
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "failed"
  const [saved, setSaved] = useState(null); // { gameId, date, opposition }

  const [players, setPlayers] = useState({ status: "loading", list: [] }); // loading | failed | ready
  const [summaries, setSummaries] = useState(null); // { [playerId]: { ok, data } } | null while loading
  const [ticked, setTicked] = useState(() => new Set());
  const [game, setGame] = useState({ status: gameId ? "loading" : "none", storedDate: null }); // none | loading | failed | ready
  const [reloadKey, setReloadKey] = useState(0);

  // #1299 (D303): Schedule game scrolls to the first missing field and focuses it.
  const rootRef = useRef(null);
  const armFocus = useFocusFirstInvalid(rootRef, errors);

  // The linked players, then each one's figures (4 at a time, never blocking the rows).
  useEffect(() => {
    let live = true;
    setPlayers({ status: "loading", list: [] });
    setSummaries(null);
    api
      .get("/players")
      .then((r) => {
        if (!live) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setPlayers({ status: "ready", list });
        return fetchPlayerSummaries(list.map((p) => p.player_id)).then((results) => {
          if (!live) return;
          const byId = {};
          for (const res of results) byId[res.playerId] = res;
          setSummaries(byId);
        });
      })
      .catch((e) => {
        if (!live) return;
        console.error("ScheduleGameForm: failed to load the players", e);
        setPlayers({ status: "failed", list: [] });
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  // `?game=<id>`: prefill from the game, once.
  useEffect(() => {
    if (!gameId) return undefined;
    let live = true;
    setGame({ status: "loading", storedDate: null });
    getGame(gameId)
      .then((g) => {
        if (!live) return;
        setForm({
          date: g.match_date || "",
          time: g.match_time || "",
          opposition: g.opponent || "",
          team: g.team || "",
          competition: g.competition || "",
        });
        setGround(g.ground_id ? { name: g.ground, id: g.ground_id } : null);
        setTicked(new Set((g.squad || []).map((m) => m.player_id)));
        setGame({ status: "ready", storedDate: g.match_date || null });
      })
      .catch((e) => {
        if (!live) return;
        console.error("ScheduleGameForm: failed to load the game", e);
        setGame({ status: "failed", storedDate: null });
      });
    return () => {
      live = false;
    };
  }, [gameId, reloadKey]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const pickGround = (name, id) => {
    setGround({ name, id });
    setErrors((x) => ({ ...x, ground: undefined }));
    setView("form");
  };

  const toggle = useCallback((id) => {
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const tickEveryone = () => setTicked(new Set(players.list.map((p) => p.player_id)));

  // Only players who are still linked can be in the squad: an id the API no longer lists is dropped.
  const squadIds = useMemo(() => players.list.filter((p) => ticked.has(p.player_id)).map((p) => p.player_id), [players.list, ticked]);

  const canSave = players.status === "ready" && squadIds.length > 0 && game.status !== "loading" && game.status !== "failed";

  const save = async () => {
    armFocus();
    const next = {};
    if (!form.date) next.date = "Pick the day";
    else if (form.date !== game.storedDate) {
      // The API windows a new date on the coach's own day; a kept date is not re-windowed.
      const today = todayIso();
      const max = planDateMax(today);
      if (form.date < today || form.date > max) next.date = `Pick a date between today and ${planDateLabel(max)}.`;
    }
    if (!form.opposition.trim()) next.opposition = "Who are you playing?";
    if (!ground) next.ground = "Choose a ground";
    if (!form.competition.trim()) next.competition = "Add the competition to schedule the game.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaveState("saving");
    const body = {
      match_date: form.date,
      opponent: form.opposition.trim(),
      competition: form.competition.trim(),
      ground_id: ground.id,
      squad: squadIds,
    };
    if (form.time) body.match_time = form.time;
    if (form.team.trim()) body.team = form.team.trim();
    try {
      const result = gameId ? await updateGame(gameId, body) : await scheduleGame(body);
      setSaved({ gameId: result?.game_id || gameId, date: form.date, opposition: body.opponent });
      setSaveState("idle");
    } catch (e) {
      console.error("ScheduleGameForm: failed to schedule the game", e);
      setSaveState("failed");
    }
  };

  if (saved) {
    return (
      <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }} data-testid="game-schedule">
        <div className="flex flex-col" style={{ gap: 4 }}>
          <div className="nw-eyebrow">Team selection</div>
          <div className="nw-title-lg" data-testid="game-title">Schedule a game</div>
        </div>
        <div
          data-testid="game-saved"
          role="status"
          style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "var(--nw-line-faint)", borderRadius: "var(--nw-radius-control)", fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)" }}
        >
          <Check size={18} aria-hidden="true" style={{ color: "var(--nw-navy)", flexShrink: 0, marginTop: 2 }} />
          <span>{`Game scheduled for ${gameDateLabel(saved.date)} vs ${saved.opposition}. The squad can see it in Coming up.`}</span>
        </div>
        {/* D259: the board draws Pick the XI as the outlined secondary (white, navy
            border), so the saved state has no gold. */}
        <SecondaryAction variant="navy-outline" data-testid="game-pick-xi" onClick={() => navigate(`/coach-portal/team?game=${encodeURIComponent(saved.gameId)}`)}>
          Pick the XI
        </SecondaryAction>
        <SecondaryAction as={Link} to={GAMES_PATH} variant="link" data-testid="game-saved-games" style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}>
          <ChevronLeft size={16} aria-hidden="true" />
          Your games
        </SecondaryAction>
      </div>
    );
  }

  if (view === "ground" || view === "add-ground") {
    return (
      <div data-testid="game-ground-step">
        {view === "add-ground" ? (
          <LogMatchAddGroundPanel seed={addSeed} future onSaved={(g) => pickGround(g.name, g.ground_id)} onCancel={() => setView("ground")} />
        ) : (
          <LogMatchGroundStep
            showSaved={false}
            presetGround={NO_PRESET}
            onPicked={pickGround}
            onAddGround={(seed) => {
              setAddSeed(seed || "");
              setView("add-ground");
            }}
            onExitStep1={() => setView("form")}
          />
        )}
      </div>
    );
  }

  if (game.status === "failed") {
    return (
      <div style={{ padding: "var(--nw-space-page)" }} data-testid="game-schedule">
        <LoadFailedCard title="Couldn't load this game" testId="game-load-failed" retryTestId="game-load-failed-retry" onRetry={() => setReloadKey((k) => k + 1)} />
      </div>
    );
  }

  return (
    <div ref={rootRef} data-testid="game-schedule" className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }} aria-busy={game.status === "loading" ? "true" : undefined}>
      <SecondaryAction
        as={Link}
        to={GAMES_PATH}
        variant="link"
        data-testid="game-back"
        style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}
      >
        <ChevronLeft size={16} aria-hidden="true" />
        Your games
      </SecondaryAction>

      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="nw-eyebrow">Team selection</div>
        <div className="nw-title-lg" data-testid="game-title">Schedule a game</div>
        <div className="nw-lede">A game your squad is going to play. Each player in the squad sees it in Coming up, scheduled by you.</div>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <Field
          label="Date"
          type="date"
          min={todayIso()}
          max={planDateMax(todayIso())}
          className="flex-1 min-w-0"
          data-testid="game-date"
          value={form.date}
          onChange={set("date")}
          error={errors.date}
        />
        <Field label="Time" type="time" className="flex-1 min-w-0" data-testid="game-time" value={form.time} onChange={set("time")} />
      </div>
      <Field
        label="Opposition"
        placeholder="Who are you playing?"
        data-testid="game-opposition"
        value={form.opposition}
        onChange={set("opposition")}
        error={errors.opposition}
      />

      <div>
        <div className="nw-label" style={{ marginBottom: 4 }}>Ground</div>
        <button
          type="button"
          data-testid="game-ground"
          data-field-invalid={errors.ground ? "true" : undefined}
          aria-describedby={errors.ground ? "game-ground-error" : undefined}
          onClick={() => setView("ground")}
          className="flex items-center justify-between"
          style={{
            width: "100%",
            height: "var(--nw-size-control)",
            padding: "0 12px",
            background: "var(--nw-surface)",
            border: errors.ground ? "var(--nw-border-invalid)" : "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-control)",
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontSize: 15,
            color: ground ? "var(--nw-ink)" : "var(--nw-ink-muted)",
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span>{ground ? ground.name : "Choose a ground"}</span>
          <ChevronRight size={18} aria-hidden="true" style={{ color: "var(--nw-ink-faint)" }} />
        </button>
        {errors.ground && (
          <FieldError id="game-ground-error" testId="game-ground-error">
            {errors.ground}
          </FieldError>
        )}
      </div>

      <Field label="Your team" placeholder="Which of your teams?" data-testid="game-team" value={form.team} onChange={set("team")} />
      <Field
        label="Competition"
        placeholder="Which competition is it?"
        data-testid="game-competition"
        value={form.competition}
        onChange={set("competition")}
        error={errors.competition}
      />

      <Squad
        players={players}
        summaries={summaries}
        ticked={ticked}
        onToggle={toggle}
        onTickEveryone={tickEveryone}
        onRetry={() => setReloadKey((k) => k + 1)}
      />

      {saveState === "failed" && (
        <div
          data-testid="game-save-failed"
          role="alert"
          style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink)", padding: "10px 12px", background: "var(--nw-line-faint)", borderLeft: "3px solid var(--nw-alert)", borderRadius: "var(--nw-radius-control)" }}
        >
          <TriangleAlert size={16} color="var(--nw-alert)" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
          <span>That didn't save. Nothing is scheduled yet — try again.</span>
        </div>
      )}

      <ActionButton
        data-testid="game-save"
        onClick={save}
        disabled={saveState === "saving" || !canSave}
        aria-busy={saveState === "saving" ? "true" : undefined}
        icon={saveState === "saving" ? <Loader2 size={18} style={{ color: "#475569" }} className="animate-spin" /> : null}
        style={saveState === "saving" ? { background: "#E2E8F0", color: "#475569", opacity: 1 } : { color: "#0F172A" }}
      >
        {saveState === "saving" ? "Scheduling…" : "Schedule game"}
      </ActionButton>
    </div>
  );
}

function SquadSkeleton() {
  const bar = (w) => <div style={{ height: 12, width: w, borderRadius: 6, background: "var(--nw-line)" }} />;
  return (
    <div data-testid="game-squad-loading" aria-busy="true" className="flex flex-col" style={{ gap: 8 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)", minHeight: 56 }}>
          {bar("45%")}
          {bar("70%")}
        </div>
      ))}
    </div>
  );
}

function Squad({ players, summaries, ticked, onToggle, onTickEveryone, onRetry }) {
  if (players.status === "loading") return <SquadSkeleton />;
  if (players.status === "failed") {
    return <LoadFailedCard title="Couldn't load your players" testId="game-squad-failed" retryTestId="game-squad-failed-retry" onRetry={onRetry} />;
  }
  if (players.list.length === 0) {
    return (
      <EmptyState
        shape="portal"
        data-testid="game-squad-empty"
        icon={<Users size={26} strokeWidth={2} />}
        line="You have no linked players yet."
        prose="A game needs a squad. Invite a player from Your players."
      />
    );
  }

  const count = players.list.filter((p) => ticked.has(p.player_id)).length;
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div className="nw-section" data-testid="game-squad-label">Squad for this game</div>
        <div className="nw-caption-strong" style={{ color: "var(--nw-navy)" }} data-testid="game-squad-count">
          {count} of {players.list.length} ticked
        </div>
      </div>
      <div className="nw-help">Everyone ticked sees the game in Coming up, and the team when you publish it.</div>
      {/* The link's 44px tap area reaches past its line (tapTarget.js); the wrapper's
          padding plus the column gap keep the first row from covering the foot of it. */}
      <div style={{ padding: "4px 0" }}>
        <SecondaryAction variant="link" data-testid="game-squad-all" data-nav="in-place" onClick={onTickEveryone} style={{ display: "flex", width: "fit-content", alignItems: "center" }}>
          Tick everyone
        </SecondaryAction>
      </div>
      {players.list.map((p) => (
        <SquadRow key={p.player_id} player={p} summary={summaries ? summaries[p.player_id] : null} on={ticked.has(p.player_id)} onToggle={() => onToggle(p.player_id)} />
      ))}
    </div>
  );
}

function SquadRow({ player, summary, on, onToggle }) {
  let line;
  if (!summary) {
    line = <span aria-hidden="true" style={{ display: "inline-block", height: 12, width: "55%", borderRadius: 6, background: "var(--nw-line)" }} />;
  } else if (!summary.ok) {
    line = "Couldn't load this player's figures";
  } else {
    line = squadFiguresLine(summary.data);
  }
  const id = player.player_id;
  return (
    <div
      data-testid={`game-squad-${id}`}
      style={{ display: "flex", alignItems: "center", gap: 4, minHeight: 56, padding: "6px 6px 6px 14px", background: "var(--nw-surface)", border: "1px solid", borderColor: on ? "var(--nw-navy)" : "var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div className="nw-rowname" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{player.full_name}</div>
        <div className="nw-help">{line}</div>
      </div>
      <button
        type="button"
        role="checkbox"
        aria-checked={on}
        aria-label={`In the squad: ${player.full_name}`}
        data-testid={`game-squad-tick-${id}`}
        data-nav="in-place"
        onClick={onToggle}
        style={{ width: 44, height: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 24,
            height: 24,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: on ? "var(--nw-navy)" : "var(--nw-surface)",
            border: on ? "2px solid var(--nw-navy)" : "2px solid var(--nw-line-strong)",
          }}
        >
          {on && <Check size={16} strokeWidth={3} style={{ color: "#FFFFFF" }} />}
        </span>
      </button>
    </div>
  );
}
